import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientApi } from '../services/api';
import Icon from '../components/Icon';

function formatCount(n) {
  if ((n || 0) >= 1000) return (n / 1000).toFixed(1).replace('.0', '') + 'K';
  return String(n || 0);
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return new Date(dateStr).toLocaleDateString();
}

const DEMO_POSTS = [
  { id: 'demo-1', is_demo: true, display_name: 'Alex Rivera', avatar_url: null, bg: '#0f3460', content: "Just crushed a 5am leg day. No excuses, no shortcuts. The grind doesn't care about your feelings — but it rewards your consistency every single time. 💪🔥", love_count: 97,  comment_count: 3, share_count: 18 },
  { id: 'demo-2', is_demo: true, display_name: 'Jordan Lee',  avatar_url: null, bg: '#1b263b', content: "PR on deadlift today — 315lbs! Six months ago I couldn't pull 185. If you're consistent and patient, the results will come. Tag your accountability partner 👇",   love_count: 201, comment_count: 8, share_count: 34 },
  { id: 'demo-3', is_demo: true, display_name: 'Maya Chen',   avatar_url: null, bg: '#2d1515', content: "Rest days are part of the program. Recovery isn't weakness — it's strategy. Ate well, slept 8 hours, walked 3 miles. Tomorrow we go hard. 🧘‍♀️",                   love_count: 445, comment_count: 6, share_count: 91 },
];

function ActionBtn({ icon, count, active, onClick }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}>
      <div style={{ width: 46, height: 46, borderRadius: '50%', background: active ? 'rgba(215,38,56,0.25)' : 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: active ? '1.5px solid rgba(215,38,56,0.6)' : '1.5px solid rgba(255,255,255,0.12)', transition: 'all 0.15s' }}>
        <img src={'/icons/' + icon} alt="" style={{ width: 22, height: 22, filter: active ? 'none' : 'brightness(0) invert(1)', opacity: active ? 1 : 0.85 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{formatCount(count)}</span>
    </button>
  );
}

function CommentSheet({ post, onClose }) {
  const [comments, setComments] = useState([]);
  const [text, setText]         = useState('');
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [error,   setError]     = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (post.is_demo) { setLoading(false); return; }
    clientApi.get('/posts/' + post.id + '/comments')
      .then(setComments).catch(() => {}).finally(() => setLoading(false));
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [post.id, post.is_demo]);

  async function submit(e) {
    if (e?.preventDefault) e.preventDefault();
    if (!text.trim() || sending || post.is_demo) return;
    setSending(true);
    try {
      const c = await clientApi.post('/posts/' + post.id + '/comments', { content: text.trim() });
      setComments(prev => [...prev, c]);
      setText('');
    } catch (err) {
      setError(err.message || 'Failed to post comment');
    } finally {
      setSending(false);
    }
  }

  const initials = name => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 400 }} />
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401, background: '#111', borderRadius: '18px 18px 0 0', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 10px', borderBottom: '1px solid #222' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Comments</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading && <div style={{ textAlign: 'center', color: '#555', fontSize: 13, padding: 24 }}>Loading…</div>}
          {error && <div style={{ background: 'rgba(215,38,56,0.15)', border: '1px solid rgba(215,38,56,0.4)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#ff6b6b', marginBottom: 8 }}>{error}</div>}
          {!loading && comments.length === 0 && !error && <div style={{ textAlign: 'center', color: '#555', fontSize: 13, padding: 24 }}>No comments yet. Be the first!</div>}
          {comments.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#D72638', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {c.avatar_url
                  ? <img src={c.avatar_url} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{initials(c.display_name)}</span>
                }
              </div>
              <div style={{ flex: 1, background: '#1A1A1A', borderRadius: 12, padding: '8px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{c.display_name}</span>
                  <span style={{ fontSize: 11, color: '#555' }}>{timeAgo(c.created_at)}</span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: '#CCC', lineHeight: 1.5 }}>{c.content}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #222', display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', boxSizing: 'border-box' }}>
          <input
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submit(e)}
            placeholder={post.is_demo ? 'Sign in to comment…' : 'Add a comment…'}
            disabled={post.is_demo}
            style={{ flex: 1, minWidth: 0, background: '#1A1A1A', border: '1px solid #2E2E2E', borderRadius: 22, padding: '10px 14px', fontSize: 14, color: '#fff', outline: 'none', boxSizing: 'border-box', opacity: post.is_demo ? 0.5 : 1 }}
          />
          <button
            onClick={submit}
            disabled={!text.trim() || sending || post.is_demo}
            style={{ background: '#D72638', border: 'none', borderRadius: '50%', width: 38, height: 38, minWidth: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: (text.trim() && !sending && !post.is_demo) ? 'pointer' : 'default', opacity: (!text.trim() || sending || post.is_demo) ? 0.5 : 1, flexShrink: 0 }}>
            <span style={{ color: '#fff', fontSize: 16, lineHeight: 1 }}>↑</span>
          </button>
        </div>
      </div>
    </>
  );
}

function NotificationPanel({ onClose }) {
  const [notifs, setNotifs]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi.get('/notifications?limit=20')
      .then(setNotifs).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function markAllRead() {
    await clientApi.put('/notifications/read-all').catch(() => {});
    setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: 60, right: 12, width: 300, background: '#111', border: '1px solid #222', borderRadius: 14, zIndex: 401, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #222', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>Notifications</span>
          <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#D72638', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Mark all read</button>
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 20, textAlign: 'center', color: '#555', fontSize: 13 }}>Loading…</div>}
          {!loading && notifs.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#555', fontSize: 13 }}>No notifications</div>}
          {notifs.map(n => (
            <div key={n.id} style={{ padding: '11px 16px', borderBottom: '1px solid #1A1A1A', background: n.is_read ? 'transparent' : 'rgba(215,38,56,0.05)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              {!n.is_read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#D72638', marginTop: 5, flexShrink: 0 }} />}
              <div style={{ flex: 1, marginLeft: n.is_read ? 17 : 0 }}>
                <div style={{ fontSize: 13, color: '#fff' }}>{n.message}</div>
                <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{timeAgo(n.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function PostCard({ post, onComment, onProfileClick }) {
  const isDemo = !!post.is_demo;
  const [loved,    setLoved]    = useState(!!post.is_loved);
  const [loves,    setLoves]    = useState(post.love_count || 0);
  const [comments, setComments] = useState(post.comment_count || 0);
  const [expanded, setExpanded] = useState(false);
  const loveInFlight = useRef(false);

  const desc   = post.content || '';
  const isLong = desc.length > 100;
  const shown  = expanded || !isLong ? desc : desc.slice(0, 100);
  const initials = (post.display_name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  async function handleLove() {
    if (isDemo || loveInFlight.current) return;
    const next = !loved;
    setLoved(next);
    setLoves(n => next ? n + 1 : Math.max(0, n - 1));
    loveInFlight.current = true;
    try {
      await clientApi.post('/social/posts/' + post.id + '/love', {});
    } catch {
      setLoved(!next);
      setLoves(n => next ? Math.max(0, n - 1) : n + 1);
    } finally {
      loveInFlight.current = false;
    }
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', flexShrink: 0, background: post.bg || '#0D0D0D', overflow: 'hidden' }}>
      {post.media_url && post.media_type === 'video' && (
        <video src={post.media_url} autoPlay loop muted playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {post.media_url && post.media_type === 'image' && (
        <img src={post.media_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.35) 0%, transparent 35%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)', pointerEvents: 'none' }} />

      <div style={{ position: 'absolute', left: 14, top: 0, bottom: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, zIndex: 10 }}>
        <ActionBtn icon="icon-love.png"    count={loves}    active={loved}  onClick={handleLove} />
        <ActionBtn icon="icon-comment.png" count={comments} active={false}  onClick={() => onComment(post, setComments)} />
        <ActionBtn icon="icon-share.png"   count={post.share_count || 0}    onClick={() => {}} />
      </div>

      <div style={{ position: 'absolute', bottom: 80, left: 0, right: 0, padding: '0 16px 0 76px', zIndex: 10 }}>
        <div
          onClick={() => post.client_profile_id && onProfileClick && onProfileClick(post.client_profile_id)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginBottom: 10, cursor: post.client_profile_id ? 'pointer' : 'default' }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{post.display_name}</span>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: post.avatar_url ? 'transparent' : '#D72638', border: '2px solid rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {post.avatar_url ? <img src={post.avatar_url} alt="" style={{ width: 38, height: 38, objectFit: 'cover' }} /> : <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{initials}</span>}
          </div>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.92)', lineHeight: 1.55, textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
          {shown}
          {isLong && !expanded && <><span>... </span><button onClick={() => setExpanded(true)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 13, cursor: 'pointer', padding: 0 }}>more</button></>}
          {isLong && expanded && <> <button onClick={() => setExpanded(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 13, cursor: 'pointer', padding: 0 }}>less</button></>}
        </p>
      </div>
    </div>
  );
}

function SocialNav({ active, onNav }) {
  const items = [
    { key: 'feed',     icon: 'icon-feed.png',              label: 'Feed' },
    { key: 'search',   icon: 'icon-social-search.png',     label: 'Search' },
    { key: 'calendar', icon: 'icon-social-calendar.png',   label: 'Workouts' },
    { key: 'profile',  icon: 'icon-social-profile.png',    label: 'Profile' },
  ];
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: 64, background: 'rgba(8,8,8,0.92)', backdropFilter: 'blur(12px)', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-around', zIndex: 100, paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {items.map(item => (
        <button key={item.key} onClick={() => onNav(item.key)}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 12px', opacity: active === item.key ? 1 : 0.4, transition: 'opacity 0.15s' }}>
          <img src={'/icons/' + item.icon} alt={item.label} style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }} />
          <span style={{ fontSize: 10, color: '#fff', fontWeight: active === item.key ? 700 : 400 }}>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

export default function SocialFeed() {
  const navigate = useNavigate();
  const [posts,       setPosts]       = useState([]);
  const [navTab,      setNavTab]      = useState('feed');
  const [commentPost, setCommentPost] = useState(null);
  const [showNotifs,  setShowNotifs]  = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    clientApi.get('/feed')
      .then(data => setPosts(data.posts?.length ? data.posts : DEMO_POSTS))
      .catch(() => setPosts(DEMO_POSTS));
  }, []);

  useEffect(() => {
    function fetchCount() {
      clientApi.get('/notifications/unread-count').then(d => setUnreadCount(d.count || 0)).catch(() => {});
    }
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, []);

  function handleComment(post) {
    setCommentPost(post);
  }

  function handleNav(key) {
    setNavTab(key);
    if (key === 'profile') navigate('/profile');
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#080808', overflow: 'hidden' }}>
      <div style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top) + 12px)', right: 14, zIndex: 200 }}>
        <button onClick={() => setShowNotifs(v => !v)}
          style={{ position: 'relative', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <Icon name="icon-social-notification.png" alt="notifications" style={{ opacity: 0.9 }} />
          {unreadCount > 0 && (
            <span style={{ position: 'absolute', top: 2, right: 2, background: '#D72638', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      <div ref={containerRef} style={{ height: '100%', overflowY: 'scroll', scrollSnapType: 'y mandatory', scrollbarWidth: 'none' }}>
        <style>{'div::-webkit-scrollbar{display:none}'}</style>
        {posts.map(post => (
          <div key={post.id} style={{ height: '100dvh', scrollSnapAlign: 'start', scrollSnapStop: 'always' }}>
            <PostCard post={post} onComment={handleComment} onProfileClick={id => navigate('/profile/' + id)} />
          </div>
        ))}
        {posts.length === 0 && (
          <div style={{ height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444', fontSize: 14 }}>No posts yet.</div>
        )}
      </div>

      <SocialNav active={navTab} onNav={handleNav} />

      {commentPost && (
        <CommentSheet post={commentPost} onClose={() => setCommentPost(null)} />
      )}

      {showNotifs && <NotificationPanel onClose={() => setShowNotifs(false)} />}
    </div>
  );
}
